// Pure helpers deciding whether an EMPLOYEE login may open a page, based on the
// `modules` the vendor granted them. Mirrors the sidebar filter in layouts/hydrogen/sidebar.tsx.

export const toModuleSlug = (str: string) => str?.toLowerCase().replace(/\s+/g, '').trim();

export function parseEmployeeModules(raw: unknown): string[] {
  let list: unknown = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw || '[]');
    } catch {
      return [];
    }
  }
  return Array.isArray(list) ? list.map((m) => toModuleSlug(String(m))) : [];
}

interface MenuLike {
  name?: string;
  href?: string;
  dropdownItems?: { href?: string }[];
}

/** href (query stripped) -> slug of the top-level menu entry that owns it. */
export function buildRouteModuleMap(items: MenuLike[]): Record<string, string> {
  const map: Record<string, string> = {};
  const clean = (href?: string) => (href && href !== '#' ? href.split('?')[0] : null);
  for (const item of items) {
    const slug = toModuleSlug(item.name || '');
    const own = clean(item.href);
    if (own && slug) map[own] = slug;
    for (const child of item.dropdownItems || []) {
      const c = clean(child.href);
      if (c && slug) map[c] = slug;
    }
  }
  return map;
}

/** href (query stripped) -> "section>page" slug for every page listed under a menu section. */
export function buildRouteChildMap(items: (MenuLike & { dropdownItems?: { href?: string; name?: string }[] })[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const item of items) {
    const section = toModuleSlug(item.name || '');
    for (const child of (item.dropdownItems || []) as { href?: string; name?: string }[]) {
      const href = child.href && child.href !== '#' ? child.href.split('?')[0] : null;
      if (href && section && child.name) map[href] = `${section}>${toModuleSlug(child.name)}`;
    }
  }
  return map;
}

/**
 * Pages not in the menu (profile, detail pages under an unknown prefix) stay open; a page matched to
 * a menu entry — exactly or by longest path prefix — needs that module. Like the sidebar, an employee
 * with no modules configured is not restricted.
 */
/** Pages every employee needs regardless of modules (e.g. linking their own WhatsApp for CRM). */
// Landing/onboarding pages are opened automatically after login, so they must never be gated.
export const EMPLOYEE_ALWAYS_OPEN = ['/', '/features', '/onboarding-setup', '/whatsapp'];
export const EMPLOYEE_ALWAYS_OPEN_PREFIXES = ['/forms/profile-settings'];

export function canEmployeeOpen(pathname: string, modules: string[], routeModuleMap: Record<string, string>, routeChildMap: Record<string, string> = {}): boolean {
  if (!modules.length) return true;
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  if (EMPLOYEE_ALWAYS_OPEN.includes(path)) return true;
  if (EMPLOYEE_ALWAYS_OPEN_PREFIXES.some((p) => path === p || path.startsWith(p + '/'))) return true;
  let owner = routeModuleMap[path];
  if (!owner) {
    let best = '';
    for (const href of Object.keys(routeModuleMap)) {
      if (href !== '/' && path.startsWith(href + '/') && href.length > best.length) best = href;
    }
    owner = best ? routeModuleMap[best] : '';
  }
  if (!owner) return true;
  if (!modules.includes(owner)) return false;
  // A section narrowed to some of its pages ("orders>pending") only opens those pages.
  const narrowed = modules.filter((m) => m.startsWith(`${owner}>`));
  if (!narrowed.length) return true;
  let child = routeChildMap[path];
  if (!child) {
    let best = '';
    for (const href of Object.keys(routeChildMap)) {
      if (routeChildMap[href].startsWith(`${owner}>`) && href !== '/' && path.startsWith(href + '/') && href.length > best.length) best = href;
    }
    child = best ? routeChildMap[best] : '';
  }
  return !child || narrowed.includes(child);
}
